import { type MouseEvent, useCallback, useState } from "react";

import type { IOverlay, OverlayKey } from "../types/common";

export interface IUseOverlaysOptions {
    /** Максимально допустимое количество оверлеев */
    maxOverlaysCount: number;
    /** Дополнительное пространство для скруглений и абсолютно позиционированных частей */
    additionalSpase: number;
}

export interface IUseOverlays {
    overlays: IOverlay[];
    addOverlay: (element: HTMLElement, event: MouseEvent) => void;
    createRemoveOverlayHandler: (overlayKey: OverlayKey) => () => void;
}

const DEFAULT_USE_OVERLAYS_OPTIONS: IUseOverlaysOptions = {
    maxOverlaysCount: 5,
    additionalSpase: 50,
};

/**
 * @description
 * Хук, который инкапсулирует в себе управление состоянием оверлеев компонента
 */
export const useOverlays = (
    options: Partial<IUseOverlaysOptions> = {},
): IUseOverlays => {
    const { maxOverlaysCount, additionalSpase } = {
        ...DEFAULT_USE_OVERLAYS_OPTIONS,
        ...options,
    };

    const [overlays, setOverlays] = useState<IOverlay[]>([]);

    const addOverlay = (element: HTMLElement, event: MouseEvent) => {
        const rect = element.getBoundingClientRect();
        const posX = event.clientX - rect.left;
        const posY = event.clientY - rect.top;
        const scaleFactor = getScaleFactor(posX, posY, rect);
        setOverlays((_oldOverlays) => {
            const oldOverlays = [..._oldOverlays];
            oldOverlays.push({
                posX,
                posY,
                scaleFactor,
                key: Date.now(),
            });

            if (oldOverlays.length > maxOverlaysCount) {
                oldOverlays.shift();
            }

            return oldOverlays;
        });
    };

    const getScaleFactor = (posX: number, posY: number, domRect: DOMRect) => {
        const horizontalScaleFactor = Math.ceil(
            Math.max(posX, domRect.width - posX),
        );
        const verticalScaleFactor = Math.ceil(
            Math.max(posY, domRect.height - posY),
        );
        /*
            Выбираем максимальный диаметр расширения + закладываем дополнительное пространство
            для скруглений и абсолютно позиционированных частей, выходящих за пределы элемента.
         */
        return (
            Math.max(horizontalScaleFactor, verticalScaleFactor) * 2 +
            additionalSpase
        );
    };

    const createRemoveOverlayHandler = useCallback(
        (overlayKey: OverlayKey) => () => {
            setOverlays((overlays) =>
                overlays.filter(({ key }) => key !== overlayKey),
            );
        },
        [],
    );

    return {
        overlays,
        addOverlay,
        createRemoveOverlayHandler,
    };
};
